import { TUser, TUserRoles } from '@/features/users/schemas/users.schema.ts';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';

type PermissionCheck<Key extends keyof Permissions> =
  | boolean
  | ((user: Pick<TUser, 'userId' | 'role'>, data?: Permissions[Key]['dataType']) => boolean);

type RolesWithPermissions = {
  [R in TUserRoles]: Partial<{
    [Key in keyof Permissions]: Partial<{
      [Action in Permissions[Key]['action']]: PermissionCheck<Key>;
    }>;
  }>;
};

export type Permissions = {
  navigation: {
    dataType: undefined;
    action:
      | 'navigation.main-menu'
      | 'navigation.main-menu.dashboard'
      | 'navigation.main-menu.plants'
      | 'navigation.main-menu.bess'
      | 'navigation.main-menu.devices'
      | 'navigation.main-menu.release-notes'
      | 'navigation.plants'
      | 'navigation.tools'
      | 'navigation.tools.event-rules'
      | 'navigation.tools.notification-rules'
      | 'navigation.admin-menu'
      | 'navigation.admin-menu.users'
      | 'navigation.admin-menu.energy-trading';
  };
  dashboard: {
    dataType: undefined;
    action: 'view-page.dashboard';
  };
  plants: {
    dataType: Pick<TPlant, 'plantId' | 'companyId'>;
    action:
      | 'view-page.plants'
      | 'view-page.plant'
      | 'view-page.control-panel'
      | 'view-page.schedule'
      | 'view-page.create'
      | 'view-page.edit'
      | 'view-page.connection-details'
      | 'view-page.user-access-management'
      | 'view-1-minute-granularity-energy-management.plant'
      | 'view-generate-report.plant'
      | 'edit.plant.production-fields'
      | 'edit.plant.storage-fields'
      | 'create'
      | 'update'
      | 'update.control-panel'
      | 'update.access-management'
      | 'delete';
  };
  bess: {
    dataType: undefined;
    action: 'view-page.bess';
  };
  devices: {
    dataType: undefined;
    action: 'view-page.devices' | 'view-page.device';
  };
  users: {
    dataType: Pick<TUser, 'userId' | 'role'>;
    action:
      | 'view-page.users'
      | 'view-page.user-access-management'
      | 'update'
      | 'update.access-management';
  };
  'event-rules': {
    dataType: undefined;
    action: 'view-page.event-rules' | 'create' | 'update' | 'delete';
  };
  'notification-rules': {
    dataType: undefined;
    action: 'view-page.notification-rules' | 'create' | 'update' | 'delete' | 'forwardTo';
  };
  'my-profile': {
    dataType: undefined;
    action: 'view-page.my-profile';
  };
  notifications: {
    dataType: undefined;
    action: 'view-page.notifications';
  };
};

export type Resource = keyof Permissions;

//
// //
//

const ROLES = {
  'Company owner': {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.event-rules': true,
      'navigation.tools.notification-rules': true,
      'navigation.admin-menu': true,
      'navigation.admin-menu.users': true,
      'navigation.admin-menu.energy-trading': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-1-minute-granularity-energy-management.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
      'view-page.user-access-management': true,
      'edit.plant.production-fields': true,
      'edit.plant.storage-fields': true,
      create: true,
      update: true,
      'update.control-panel': true,
      'update.access-management': true,
      delete: true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    users: {
      'view-page.users': true,
      'view-page.user-access-management': true,
      update: true,
      'update.access-management': true,
    },
    'event-rules': {
      'view-page.event-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
      forwardTo: true,
    },
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  'Company administrator': {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.event-rules': true,
      'navigation.tools.notification-rules': true,
      'navigation.admin-menu': true,
      'navigation.admin-menu.users': true,
      'navigation.admin-menu.energy-trading': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-1-minute-granularity-energy-management.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
      'view-page.user-access-management': true,
      'edit.plant.production-fields': true,
      'edit.plant.storage-fields': true,
      create: true,
      update: true,
      'update.control-panel': true,
      'update.access-management': true,
      delete: true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    users: {
      'view-page.users': true,
      'view-page.user-access-management': true,
      update: true,
      'update.access-management': true,
    },
    'event-rules': {
      'view-page.event-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
      forwardTo: true,
    },
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  Installer: {
    navigation: {
      'navigation.main-menu': true,
      'navigation.main-menu.dashboard': true,
      'navigation.main-menu.plants': true,
      'navigation.main-menu.bess': true,
      'navigation.main-menu.devices': true,
      'navigation.main-menu.release-notes': true,
      'navigation.tools': true,
      'navigation.tools.event-rules': true,
      'navigation.tools.notification-rules': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-page.plants': true,
      'view-page.plant': true,
      'view-1-minute-granularity-energy-management.plant': true,
      'view-page.control-panel': true,
      'view-generate-report.plant': true,
      'view-page.schedule': true,
      'view-page.create': true,
      'view-page.edit': true,
      'view-page.connection-details': true,
    },
    bess: {
      'view-page.bess': true,
    },
    devices: {
      'view-page.devices': true,
      'view-page.device': true,
    },
    'notification-rules': {
      'view-page.notification-rules': true,
      create: true,
      update: true,
      delete: true,
    },
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  Manufacturer: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {},
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  Owner: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-generate-report.plant': true,
    },
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  Guest: {
    navigation: {
      'navigation.plants': true,
    },
    dashboard: {
      'view-page.dashboard': true,
    },
    plants: {
      'view-generate-report.plant': true,
    },
    devices: {},
    users: {},
    'my-profile': {
      'view-page.my-profile': true,
    },
    notifications: {
      'view-page.notifications': true,
    },
  },

  Unknown: {
    navigation: {},
    dashboard: {},
    plants: {},
    devices: {},
    users: {},
    'my-profile': {},
    notifications: {},
  },
} as const satisfies RolesWithPermissions;

export function hasPermission<Resource extends keyof Permissions>(
  user: Pick<TUser, 'userId' | 'role'>,
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType']
) {
  const permission = (ROLES as RolesWithPermissions)[user.role][resource]?.[action];
  if (permission == null) return false;

  if (typeof permission === 'boolean') return permission;
  return data != null && permission(user, data);
}

export function hasPermissions<Resource extends keyof Permissions>(
  user: Pick<TUser, 'userId' | 'role'>,
  checks: Array<
    | [Resource, Permissions[Resource]['action']]
    | [Resource, Permissions[Resource]['action'], Permissions[Resource]['dataType']]
  >
): boolean[] {
  return checks.map(([resource, action, data]) => hasPermission(user, resource, action, data));
}
